/**
 * ExpirationConfigSection - Configuração de expiração de atendimento
 *
 * Permite habilitar/desabilitar a expiração automática de atendimentos
 * quando o cliente não responde após um período determinado.
 */

import React from 'react';
import type { UpdateChannelRequest, ChannelExpirationConfig } from '@troia-v3/types';
import type { ChannelResponse } from '@/modules/channels/types';
import { Card, CardHeader, CardContent } from '@/shared/components/ui/Card';
import { Switch } from '@/shared/components/ui/Switch';
import { Icon } from '@/shared/icons'
interface ExpirationConfigSectionProps {
  channel: ChannelResponse;
  formData: Partial<UpdateChannelRequest>;
  onFieldChange: (field: keyof UpdateChannelRequest, value: any) => void;
}

export const ExpirationConfigSection: React.FC<ExpirationConfigSectionProps> = ({
  channel,
  formData,
  onFieldChange,
}) => {
  // Get current expiration config from formData or channel
  const currentConfig: ChannelExpirationConfig = formData.expirationConfig ||
    channel.expirationConfig ||
    { enabled: false, expirationMinutes: 60 };

  const isEnabled = currentConfig.enabled;

  const handleToggle = () => {
    const newConfig: ChannelExpirationConfig = {
      ...currentConfig,
      enabled: !isEnabled,
    };
    onFieldChange('expirationConfig', newConfig);
  };

  const handleMinutesChange = (value: string) => {
    const minutes = parseInt(value, 10) || 0;
    const newConfig: ChannelExpirationConfig = {
      ...currentConfig,
      expirationMinutes: minutes,
    };
    onFieldChange('expirationConfig', newConfig);
  };

  const handleMessageChange = (value: string) => {
    const newConfig: ChannelExpirationConfig = {
      ...currentConfig,
      expirationMessage: value || undefined,
    };
    onFieldChange('expirationConfig', newConfig);
  };

  return (
    <Card>
      <CardHeader className="pb-3 px-4 py-3">
        <div className="flex items-center gap-2">
          <Icon name="clock" className="w-4 h-4" style={{ color: 'var(--theme-button-primary)' }} />
          <h3 className="font-bold text-[15px]" style={{ color: 'var(--theme-card-text)' }}>
            Expiração de Atendimento
          </h3>
        </div>
      </CardHeader>

      <CardContent className="px-4 pb-4 space-y-4">
        {/* Toggle de Expiração */}
        <div className="flex items-start justify-between">
          <div className="flex-1">
            <label
              htmlFor="expiration-toggle"
              className="block text-sm font-medium mb-1"
              style={{ color: 'var(--theme-card-text)' }}
            >
              Expirar atendimentos automaticamente
            </label>
            <p className="text-xs" style={{ color: 'var(--theme-card-muted)' }}>
              Quando habilitado, atendimentos sem resposta do cliente serão finalizados automaticamente
            </p>
          </div>

          {/* Toggle Switch — shared 38×22 (F82) */}
          <span data-testid="expiration-toggle">
            <Switch id="expiration-toggle" checked={isEnabled} onChange={handleToggle} />
          </span>
        </div>

        {/* Configurações quando habilitado */}
        {isEnabled && (
          <div className="space-y-4 pt-4 border-t" style={{ borderColor: 'var(--theme-card-border)' }}>
            {/* Tempo de expiração */}
            <div>
              <label
                htmlFor="expiration-minutes"
                className="block text-sm font-medium mb-2"
                style={{ color: 'var(--theme-card-text)' }}
              >
                Tempo de expiração (em minutos)
              </label>
              <input
                id="expiration-minutes"
                type="number"
                min="1"
                max="10080"
                value={currentConfig.expirationMinutes || 60}
                onChange={(e) => handleMinutesChange(e.target.value)}
                className="w-full px-3 py-2 rounded-lg border text-sm h-[38px]"
                style={{
                  backgroundColor: 'var(--theme-input-background)',
                  borderColor: 'var(--theme-input-border)',
                  color: 'var(--theme-input-text)',
                }}
                onFocus={(e) => {
                  e.currentTarget.style.borderColor = 'var(--theme-input-focus)';
                }}
                onBlur={(e) => {
                  e.currentTarget.style.borderColor = 'var(--theme-input-border)';
                }}
              />
              <p className="text-xs mt-1" style={{ color: 'var(--theme-card-muted)' }}>
                Tempo contado a partir da última mensagem do operador sem resposta do cliente
              </p>
            </div>

            {/* Mensagem de expiração */}
            <div>
              <label
                htmlFor="expiration-message"
                className="block text-sm font-medium mb-2"
                style={{ color: 'var(--theme-card-text)' }}
              >
                Mensagem de encerramento (opcional)
              </label>
              <textarea
                id="expiration-message"
                value={currentConfig.expirationMessage || ''}
                onChange={(e) => handleMessageChange(e.target.value)}
                placeholder="Ex: Seu atendimento foi encerrado por inatividade. Se precisar, inicie uma nova conversa."
                rows={3}
                maxLength={1000}
                className="w-full px-3 py-2 rounded-lg border text-sm resize-none"
                style={{
                  backgroundColor: 'var(--theme-input-background)',
                  borderColor: 'var(--theme-input-border)',
                  color: 'var(--theme-input-text)',
                }}
                onFocus={(e) => {
                  e.currentTarget.style.borderColor = 'var(--theme-input-focus)';
                }}
                onBlur={(e) => {
                  e.currentTarget.style.borderColor = 'var(--theme-input-border)';
                }}
              />
              <p className="text-xs mt-1" style={{ color: 'var(--theme-card-muted)' }}>
                Se preenchida, esta mensagem será enviada ao cliente antes de encerrar o atendimento
              </p>
            </div>

            {/* Info box */}
            <div
              className="p-3 rounded-lg border"
              style={{
                backgroundColor: 'var(--theme-warning-background)',
                borderColor: 'var(--theme-warning-color)',
              }}
            >
              <div className="flex items-start gap-2">
                <Icon name="alert-circle" className="w-4 h-4 mt-0.5 flex-shrink-0"
                  style={{ color: 'var(--theme-warning-color)' }} />
                <div>
                  <p className="text-sm font-medium" style={{ color: 'var(--theme-card-text)' }}>
                    Como funciona
                  </p>
                  <p className="text-xs mt-1" style={{ color: 'var(--theme-card-muted)' }}>
                    O sistema verifica a cada minuto os atendimentos em aberto.
                    Quando um atendimento ultrapassa o tempo configurado sem resposta do cliente,
                    a mensagem de encerramento é enviada (se configurada) e o atendimento é finalizado automaticamente.
                  </p>
                </div>
              </div>
            </div>
          </div>
        )}

        {/* Informação quando desabilitado */}
        {!isEnabled && (
          <div
            className="mt-4 p-3 rounded-lg border"
            style={{
              backgroundColor: 'var(--theme-card-background)',
              borderColor: 'var(--theme-card-border)',
            }}
          >
            <p className="text-xs" style={{ color: 'var(--theme-card-muted)' }}>
              Os atendimentos permanecerão abertos até serem finalizados manualmente por um operador
            </p>
          </div>
        )}
      </CardContent>
    </Card>
  );
};

ExpirationConfigSection.displayName = 'ExpirationConfigSection';
